/**
 * Lectura de pedidos de formulario COD desde el dominio único `orders_v2`.
 *
 * Reconstruye la forma plana que antes exponía la vista compat `cod_orders`
 * (espeja su DDL: `orders_v2 v` + `order_delivery_profile p` + primera `order_lines l`),
 * de modo que los lectores que aún esperan esa forma migren a `orders_v2` sin
 * reescribir sus tipos ni su render.
 *
 * Es un shim de LECTURA interino y borrable: cuando cada consumidor pase a la
 * forma nativa de orders_v2, este archivo se elimina. No introduce dependencia
 * de la vista SQL (lee la tabla real), así que habilita el drop de `cod_orders`.
 *
 * Tolerante: cada consumidor puede pedir solo las columnas/embeds que use; los
 * campos no seleccionados quedan en null/valor por defecto.
 */

/** Select que trae TODOS los campos de la forma legacy (los consumidores pueden usar uno más chico). */
export const COD_V2_FULL_SELECT =
  'id, tenant_id, customer_phone, subtotal, shipping_total, discount_total, grand_total, amount_paid, amount_due, payment_status, shipping_status, notes, created_at, updated_at, ' +
  // No se piden columnas del perfil que están vacías en TODAS las filas y sin consumidor
  // (fiscal_address, discount_code, courier_service_id, shalom_status, payload) — se dropean
  // por migración una vez desplegado esto. billing_doc_* SÍ se piden: son el respaldo fiscal
  // del checkout online (emitirComprobante/notaCredito), vacío pero vivo. Ver ADR-011.
  'order_delivery_profile(first_name, last_name, doc_number, department, province, district, address, address_ref, expected_advance, cod_status, cod_collection_status, commitment_date, commitment_notes, source, business_name, billing_doc_type, billing_doc_number), ' +
  'order_lines(product_id, name, qty, unit_price, created_at)';

interface OrdersV2CodRow {
  id: string;
  tenant_id?: string | null;
  customer_phone?: string | null;
  subtotal?: number | null;
  shipping_total?: number | null;
  discount_total?: number | null;
  grand_total?: number | null;
  amount_paid?: number | null;
  amount_due?: number | null;
  payment_status?: string | null;
  shipping_status?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  order_delivery_profile?: {
    first_name?: string | null; last_name?: string | null; doc_number?: string | null;
    department?: string | null; province?: string | null; district?: string | null;
    address?: string | null; address_ref?: string | null;
    /** El adelanto que se ESPERA. Vive acá, no en `orders_v2.amount_paid`, que es lo COBRADO. */
    expected_advance?: number | null;
    cod_status?: string | null; cod_collection_status?: string | null;
    commitment_date?: string | null; commitment_notes?: string | null;
    source?: string | null;
    business_name?: string | null;
    billing_doc_type?: string | null; billing_doc_number?: string | null;
  } | null;
  order_lines?: Array<{
    product_id?: string | null; name?: string | null; qty?: number | null;
    unit_price?: number | null; created_at?: string | null;
  }> | null;
}

/** Forma plana equivalente a la antigua vista `cod_orders`. */
export interface CodOrderFlat {
  id: string;
  tenant_id: string | null;
  product_id: string | null;
  product_name: string | null;
  quantity: number;
  unit_price: number;
  subtotal: number | null;
  shipping_amount: number | null;
  discount_amount: number | null;
  total: number | null;
  dni: string | null;
  first_name: string | null;
  last_name: string | null;
  whatsapp: string | null;
  department: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  notes: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
  amount_advance: number | null;
  amount_remaining: number | null;
  payment_status: string | null;
  collection_status: string;
  shipping_status: string | null;
  commitment_date: string | null;
  commitment_notes: string | null;
  source: string;
  document_type: string | null;
  document_number: string | null;
  business_name: string | null;
  address_reference: string | null;
}

/** Mapea una fila de orders_v2 (con embeds) a la forma plana legacy de cod_orders. */
export function mapCodV2Row(row: unknown): CodOrderFlat {
  const r = (row ?? {}) as OrdersV2CodRow;
  const p = r.order_delivery_profile ?? null;
  const lines = Array.isArray(r.order_lines) ? r.order_lines : [];
  // La vista cod_orders toma la primera línea por created_at.
  const first = lines.length > 0
    ? [...lines].sort((a, b) => String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')))[0]
    : null;
  return {
    id: r.id,
    tenant_id: r.tenant_id ?? null,
    product_id: first?.product_id ?? null,
    product_name: first?.name ?? null,
    quantity: Math.round(Number(first?.qty ?? 1)),
    unit_price: Number(first?.unit_price ?? 0),
    subtotal: r.subtotal ?? null,
    shipping_amount: r.shipping_total ?? null,
    discount_amount: r.discount_total ?? null,
    total: r.grand_total ?? null,
    dni: p?.doc_number ?? null,
    first_name: p?.first_name ?? null,
    last_name: p?.last_name ?? null,
    whatsapp: r.customer_phone ?? null,
    department: p?.department ?? null,
    province: p?.province ?? null,
    district: p?.district ?? null,
    address: p?.address ?? null,
    notes: r.notes ?? null,
    status: p?.cod_status ?? 'pending',
    created_at: r.created_at ?? null,
    updated_at: r.updated_at ?? null,
    /*
     * 🚨 ESTO LEÍA `amount_paid`, LA COLUMNA DE LO COBRADO. La forma legacy llama `amount_advance`
     * al adelanto ESPERADO, y funcionaba porque el pedido nacía con ese número escrito en la
     * columna equivocada. Desde el 23-ago `amount_paid` dice la verdad —lo que entró— así que el
     * adelanto esperado hay que leerlo de donde vive.
     */
    amount_advance: p?.expected_advance ?? null,
    amount_remaining: r.amount_due ?? null,
    payment_status: r.payment_status === 'unpaid' ? 'pending' : (r.payment_status ?? null),
    collection_status: p?.cod_collection_status ?? 'pendiente_pago_restante',
    shipping_status: r.shipping_status ?? null,
    commitment_date: p?.commitment_date ?? null,
    commitment_notes: p?.commitment_notes ?? null,
    source: p?.source ?? 'form',
    document_type: p?.billing_doc_type ?? null,
    document_number: p?.billing_doc_number ?? null,
    business_name: p?.business_name ?? null,
    address_reference: p?.address_ref ?? null,
  };
}

/** Mapea un arreglo de filas orders_v2 a la forma plana legacy. */
export function mapCodV2Rows(rows: unknown): CodOrderFlat[] {
  if (!Array.isArray(rows)) return [];
  return rows.map(mapCodV2Row);
}
